/**
 * Renders a feedback form from a DEFINITION — no question is written here.
 *
 * This is the widget, as a component. Everything it draws comes from
 * `definition.questions`: the wording, the order, the control, the options, the
 * scale, whether a comment box appears, and whether the question is shown at all.
 * That is the whole point of the feedback model — one renderer serves every
 * definition, and adding a question is a data change.
 *
 * The control chosen per question is derived from the question's TYPE CODE, never
 * from a hardcoded list of types, so a new `*_SELECT` type added through the Value
 * Sets screen renders correctly with no change here.
 */
import { useMemo } from 'react';
import { Dropdown } from 'sharedUi/Dropdown';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { Button } from 'sharedUi/Button';
import { InputSwitch } from 'sharedUi/InputSwitch';
import FormField from '../../components/common/form-field';
import NumberStepper from '../../components/common/number-stepper';
import type { FeedbackMaster, FeedbackQuestion } from '../../types/feedback-master.types';
import { useSentiments } from './use-feedback-event-value-sets';
import { SENTIMENT_ICON, SENTIMENT_ON } from './feedback-display';
import { isVisible, type AnswerMap } from './question-visibility';

// Re-exported: every caller already imports AnswerMap from the renderer, and the
// type belongs with the visibility rule that reads it.
export type { AnswerMap } from './question-visibility';

interface Props {
  definition: FeedbackMaster;
  answers: AnswerMap;
  onChange: (next: AnswerMap) => void;
  /** Show the red "required" errors — only after the user has tried to submit. */
  submitted?: boolean;
  errors?: Record<string, string>;
}

/** Derived from the CODE, so a new select-like type needs no change here. */
const isChoice = (t?: string) => !!t && t.toUpperCase().endsWith('_SELECT');
const isMulti = (t?: string) => (t || '').toUpperCase() === 'MULTI_SELECT';
const upper = (t?: string | null) => (t || '').toUpperCase();

const FeedbackFormRenderer = ({ definition, answers, onChange, submitted, errors = {} }: Props) => {
  const { options: sentimentOptions } = useSentiments();

  const questions = useMemo(
    () => [...(definition.questions || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [definition.questions],
  );

  const set = (code: string, value: unknown) =>
    onChange({ ...answers, [code]: { ...answers[code], value } });
  const setComment = (code: string, comment: string) =>
    onChange({ ...answers, [code]: { ...answers[code], comment } });

  const control = (q: FeedbackQuestion) => {
    const code = q.questionCode;
    const current = answers[code]?.value;
    const type = upper(q.questionType);

    if (type === 'SENTIMENT') {
      // Options come from the feedbackSentiment value set, not from the question:
      // a sentiment question carries no options of its own, which is what keeps
      // every sentiment figure comparable across definitions.
      //
      // Icons are looked up per CODE and fall back to the code's own label, so a
      // fourth sentiment added through Value Sets still renders — it just gets a
      // worded button until an icon is chosen for it here.
      return (
        <div className="flex flex-wrap items-center gap-2">
          {sentimentOptions.map((o) => {
            const icon = SENTIMENT_ICON[o.value];
            const on = current === o.value;
            return (
              <button key={o.value} type="button"
                aria-label={o.label} title={o.label} aria-pressed={on}
                onClick={() => set(code, on ? undefined : o.value)}
                className={`flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors
                  ${on ? SENTIMENT_ON[o.value] || 'border-indigo-500 bg-indigo-50 text-indigo-600'
                       : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600 dark:border-gray-600 dark:hover:border-gray-500'}`}>
                {icon
                  ? <i className={`pi ${icon} text-lg`} />
                  : <span className="px-1 text-[11px] font-semibold">{o.label}</span>}
              </button>
            );
          })}
        </div>
      );
    }

    if (type === 'RATING') {
      const scale = q.ratingScale ?? 5;
      const chosen = typeof current === 'number' ? current : 0;
      return (
        <div className="flex flex-wrap items-center gap-1">
          {Array.from({ length: scale }, (_, i) => i + 1).map((n) => (
            <button key={n} type="button"
              aria-label={`${n} of ${scale}`} title={`${n} of ${scale}`}
              // Clicking the current value clears it — otherwise a rating given
              // by accident can never be taken back on an optional question.
              onClick={() => set(code, chosen === n ? undefined : n)}
              className="p-0.5 text-2xl leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400">
              <i className={`pi ${n <= chosen ? 'pi-star-fill text-amber-400' : 'pi-star text-gray-300 dark:text-gray-600'}`} />
            </button>
          ))}
          <span className="ml-2 self-center text-xs text-gray-400">
            {chosen ? `${chosen} of ${scale}` : `out of ${scale}`}
          </span>
        </div>
      );
    }

    if (type === 'BOOLEAN') {
      return (
        <div className="flex h-[38px] items-center gap-3">
          <InputSwitch checked={current === true} onChange={(e) => set(code, !!e.value)} />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {current === true ? 'Yes' : 'No'}
          </span>
        </div>
      );
    }

    if (isChoice(q.questionType)) {
      const opts = (q.options || [])
        .filter((o) => o.isActive !== false)
        .map((o) => ({ label: o.label || o.value, value: o.value }));
      if (isMulti(q.questionType)) {
        const chosen: string[] = Array.isArray(current) ? (current as string[]) : [];
        return (
          <div className="flex flex-wrap gap-2">
            {opts.map((o) => {
              const on = chosen.includes(o.value);
              return (
                <Button key={o.value} label={o.label} size="small" outlined={!on}
                  onClick={() => set(code, on
                    ? chosen.filter((x) => x !== o.value)
                    : [...chosen, o.value])} />
              );
            })}
            {!opts.length && (
              <span className="text-xs text-amber-600">This question has no options configured.</span>
            )}
          </div>
        );
      }
      return (
        <Dropdown value={(current as string) || ''} options={opts}
          optionLabel="label" optionValue="value" className="w-full"
          placeholder="Choose one"
          onChange={(e: { value: string }) => set(code, e.value)} />
      );
    }

    if (type === 'NUMBER') {
      return (
        <NumberStepper value={(current as number) ?? null}
          min={q.minValue ?? undefined} max={q.maxValue ?? undefined}
          ariaLabel={q.label}
          onChange={(v) => set(code, v ?? undefined)} />
      );
    }

    // TEXT and anything new: a text box is the safe fallback, so an unfamiliar
    // type is answerable rather than un-renderable.
    return (
      <TextArea rows={3} value={(current as string) || ''} className="w-full text-sm"
        onChange={(v: string) => set(code, v)} />
    );
  };

  return (
    <div className="flex flex-col gap-4">

      {questions.filter((q) => isVisible(q, answers)).map((q) => (
        <FormField key={q.questionCode} label={q.label} required={q.required}
          error={submitted ? errors[q.questionCode] : undefined}
          hint={q.helpText || undefined}>
          {control(q)}
          {q.allowComment && (
            <div className="mt-2">
              <Input name={`c-${q.questionCode}`} value={answers[q.questionCode]?.comment || ''}
                className="w-full" placeholder="Add a comment (optional)"
                onChange={(v: string) => setComment(q.questionCode, v)} />
            </div>
          )}
        </FormField>
      ))}

      {!questions.length && (
        <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500 dark:border-gray-600">
          This definition asks nothing — it has no questions.
        </div>
      )}
    </div>
  );
};

export default FeedbackFormRenderer;
